import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Eye, EyeOff, ArrowRight, ChevronLeft } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../contexts/AuthContext";

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f9fafb;
`;

const Back = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
`;

const Center = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px 16px;

  @media (min-width: 640px) {
    padding-inline: 24px;
  }
`;

const Card = styled.div`
  background: white;
  padding: 32px 24px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  max-width: 420px;
  margin: 32px auto 0;
`;

const Title = styled.h2`
  margin: 24px 0 0;
  text-align: center;
  font-size: 28px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const Sub = styled.p`
  margin: 8px 0 0;
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.body};
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.body};
  display: block;
  margin-bottom: 6px;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const SmallLink = styled.a`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary.light};
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.primary.mid};
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.body};
  margin-bottom: 20px;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 16px;
  height: 16px;
  border-radius: 4px;
`;

const DividerWrap = styled.div`
  margin: 24px 0 0;
  position: relative;
  text-align: center;
`;

const DividerLine = styled.div`
  position: absolute;
  inset: 50% 0 auto;
  height: 1px;
  background: #e5e7eb;
`;

const DividerLabel = styled.span`
  position: relative;
  padding: 0 8px;
  background: white;
  font-size: 13px;
  color: #6b7280;
`;

const SocialGrid = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login, isAuthenticated, loading: isLoading, error: authError, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const from = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    if (isAuthenticated) {
      toast({ title: "Login successful", description: "Welcome back to Kliq CRM!" });
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from, toast]);

  useEffect(() => {
    if (authError) toast({ title: "Login failed", description: authError, variant: "destructive" });
  }, [authError, toast]);

  if (isAuthenticated) return null;

  function handleSubmit(e) {
    e.preventDefault();
    clearError();
    login({ email, password });
  }

  const error = authError;

  return (
    <Wrap>
      <Back>
        <Button
          $variant="ghost"
          $size="sm"
          onClick={() => navigate("/")}
        >
          <ChevronLeft size={16} />
          {t("auth.backToHome")}
        </Button>
      </Back>

      <Center>
        <div style={{ maxWidth: 420, margin: "0 auto" }}>
          <Title>{t("auth.signInToAccount")}</Title>
          <Sub>
            {t("auth.or")}{" "}
            <Link
              to="/signup"
              style={{
                color: "#9b87f5",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              {t("auth.createNewAccount")}
            </Link>
          </Sub>
        </div>

        <Card>
          <form onSubmit={handleSubmit}>
            {error && <p style={{ color: "#dc2626", fontSize: 14, marginBottom: 16 }}>{error}</p>}
            <Field>
              <Label>{t("auth.emailAddress")}</Label>
              <Input
                type="email"
                placeholder={t("auth.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>

            <Field>
              <Row>
                <Label>{t("auth.password")}</Label>
                <SmallLink href="#">{t("auth.forgotPassword")}</SmallLink>
              </Row>
              <div style={{ position: "relative" }}>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ paddingRight: 36 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={{
                    position: "absolute",
                    insetBlock: 0,
                    right: 10,
                    display: "flex",
                    alignItems: "center",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                  }}
                >
                  {showPassword ? (
                    <EyeOff size={16} color="#9ca3af" />
                  ) : (
                    <Eye size={16} color="#9ca3af" />
                  )}
                </button>
              </div>
            </Field>

            <CheckboxRow>
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>{t("auth.rememberMe")}</span>
            </CheckboxRow>

            <Button
              type="submit"
              disabled={isLoading}
              style={{ width: "100%", marginTop: 4 }}
            >
              {isLoading ? t("auth.signingIn") : t("auth.signIn")}
              <ArrowRight size={16} />
            </Button>
          </form>

          <DividerWrap>
            <DividerLine />
            <DividerLabel>{t("auth.orContinueWith")}</DividerLabel>
          </DividerWrap>

          <SocialGrid>
            <Button $variant="outline">{t("auth.google")}</Button>
            <Button $variant="outline">{t("auth.github")}</Button>
          </SocialGrid>

          <div
            style={{
              marginTop: 24,
              textAlign: "center",
              fontSize: 13,
              color: "#6b7280",
            }}
          >
            <Link
              to="/"
              style={{
                color: "#9b87f5",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              {t("auth.backToHomePage")}
            </Link>
          </div>
        </Card>
      </Center>
    </Wrap>
  );
}

